import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalName } from '../../../../enums';
import { useSendEmail } from '../../../../features/app';
import { useCloseModal } from '../../../../features/modals';
import { areValuesValid } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';
import { IFeatureFeedbackModalBodyProps } from './types';

export const FeatureFeedbackModal = () => {
  const { t } = useTranslation();
  const [body, setBody] = useState<IFeatureFeedbackModalBodyProps>({
    name: '',
    email: '',
    message: '',
  });
  const closeModal = useCloseModal(ModalName.FEATURE_FEEDBACK);
  const sendEmail = useSendEmail();

  const isValid = useMemo(
    () => areValuesValid([body.name, body.message, { type: 'email', value: body.email }]),
    [body],
  );

  const setName = (name: string) => setBody({ ...body, name });
  const setEmail = (email: string) => setBody({ ...body, email });
  const setMessage = (message: string) => setBody({ ...body, message });

  const send = useCallback(() => {
    const bodyString = `
      Name: ${body.name} <br>
      Email: ${body.email} <br>
      Message: ${body.message}
    `;
    const params = {
      subject: 'Feature Requested',
      body: bodyString,
    };
    sendEmail(params);
    closeModal();
  }, [sendEmail, closeModal, body]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button text={t('modal.feature.button.send')} disabled={!isValid} onClick={send} />
        <Button text={t('modal.feature.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, send, isValid]);

  return (
    <Modal name={ModalName.FEATURE_FEEDBACK} header={t('modal.feature.title')} footer={footer}>
      <p className="typography-paragraph-3 text-text-secondary mb-6">{t('modal.feature.description')}</p>

      <FormFields>
        <Input
          value={body.name}
          label={t('modal.feature.field.name.label')}
          onChange={setName}
          placeholder={t('modal.feature.field.name.placeholder')}
        />

        <Input
          value={body.email}
          label={t('modal.feature.field.email.label')}
          onChange={setEmail}
          placeholder={t('modal.feature.field.email.placeholder')}
          type="email"
        />

        <Input
          value={body.message}
          label={t('modal.feature.field.message.label')}
          onChange={setMessage}
          placeholder={t('modal.feature.field.message.placeholder')}
          type="textarea"
        />
      </FormFields>
    </Modal>
  );
};
